import { challengeRepository, progressStateRepository } from '../db/database';
import {
  CHALLENGE_CANCEL_PENALTY,
  DAYS_PER_CHALLENGE_MONTH,
  type Challenge,
  type ChallengeDurationUnit,
  type IsoDate,
} from '../models/types';
import { addDays } from '../utils/date';
import { getOrCreateProgressState } from './dailyRollover';
import { applyDeltaToBar } from './scoring';

/**
 * Challenge mantığı — CLAUDE.md §7.4.
 *
 * Challenge'lar Başarı Puanı'na KATKI SAĞLAMAZ (§7.2 formülüne
 * girmezler). Tek etkileri §7.3'teki Day Streak hesabına "1 alışkanlık
 * yapılmış gibi" eklenmeleri — bir de iptal edildiklerinde uygulanan
 * sabit puan cezası.
 */

/* ==================================================================
   Süre hesabı
   ================================================================== */

/**
 * Kullanıcının girdiği süreyi gün cinsine çevirir.
 *
 * §7.4: "1 ay" her zaman sabit 30 gündür — başlangıç tarihinin denk
 * geldiği ayın gerçek gün sayısından (28, 30, 31) bağımsız. Böylece
 * Şubat'ta başlayan bir challenge, Mart'ta başlayandan kısa olmaz.
 */
export function toDurationDays(value: number, unit: ChallengeDurationUnit): number {
  return unit === 'month' ? value * DAYS_PER_CHALLENGE_MONTH : value;
}

/**
 * Bitiş tarihi — başlangıç günü DAHİL sayılır.
 *
 * 1 Eylül'de başlayan 30 günlük bir challenge 30 Eylül'de biter,
 * 1 Ekim'de değil; bu yüzden `-1` var.
 */
export function calculateEndDate(startDate: IsoDate, durationDays: number): IsoDate {
  return addDays(startDate, durationDays - 1);
}

/**
 * Challenge'ın süresi dolmuş mu?
 * Bitiş günü dahil olduğu için, ancak o günden SONRA dolmuş sayılır.
 */
export function isExpired(challenge: Challenge, today: IsoDate): boolean {
  return today > challenge.endDate;
}

/** İlerleme oranı (0–1) — işaretlenen gün sayısının süreye oranı. */
export function getProgressRatio(challenge: Challenge): number {
  if (challenge.durationDays <= 0) return 0;
  // Kullanıcı bitiş gününden sonra da işaretlemiş olamaz, ama halkanın
  // taşmaması için yine de sınırlıyoruz.
  return Math.min(1, challenge.completedDates.length / challenge.durationDays);
}

/* ==================================================================
   Sorgular
   ================================================================== */

/** Devam eden challenge (§7.4 gereği en fazla 1 tane olabilir). */
export async function getActiveChallenge(): Promise<Challenge | undefined> {
  return challengeRepository.getActive();
}

/** Challenge bugün tamamlanmış olarak işaretlenmiş mi? */
export function isCompletedOn(challenge: Challenge, date: IsoDate): boolean {
  return challenge.completedDates.includes(date);
}

/**
 * Kullanıcı bugünü işaretleyebilir mi?
 * Challenge başlamadan önceki ya da bittikten sonraki günler
 * işaretlenemez.
 */
export function isWithinRange(challenge: Challenge, date: IsoDate): boolean {
  return date >= challenge.startDate && date <= challenge.endDate;
}

/* ==================================================================
   Durum değiştiren işlemler
   ================================================================== */

/**
 * Süresi dolmuş aktif challenge'ı kapatır (§7.4).
 *
 * Bitiş TAKVİM günüyle belirlenir: kullanıcı kaç günü işaretlediyse
 * işaretlesin, bitiş tarihi geçtiğinde challenge tamamlanmış sayılır.
 * Uygulama açılışında ve Challenge sayfası her yüklendiğinde çağrılır
 * — kullanıcı süresi dolmuş bir challenge'ı "hâlâ aktif" görmemeli.
 *
 * Kapatılan challenge döndürülür (bir şey değişmediyse `undefined`).
 */
export async function completeExpiredChallenge(
  today: IsoDate,
): Promise<Challenge | undefined> {
  const active = await challengeRepository.getActive();
  if (!active || !isExpired(active, today)) return undefined;

  const completed: Challenge = { ...active, status: 'completed' };
  await challengeRepository.upsert(completed);
  return completed;
}

/**
 * Bugünü tamamlandı olarak işaretler ya da işareti geri alır.
 *
 * Geri alma, yanlış dokunuşu düzeltebilmek için var. Yalnızca
 * işaretlenmiş günün kendisi kaldırılır; geçmiş günlere dokunulmaz.
 */
export async function toggleChallengeDay(
  challenge: Challenge,
  date: IsoDate,
): Promise<Challenge> {
  const alreadyDone = isCompletedOn(challenge, date);

  const completedDates = alreadyDone
    ? challenge.completedDates.filter((completed) => completed !== date)
    : // Tarihleri sıralı tutuyoruz ki ilerleme listesi okunabilir kalsın.
      [...challenge.completedDates, date].sort();

  const updated: Challenge = { ...challenge, completedDates };
  await challengeRepository.upsert(updated);
  return updated;
}

/**
 * Aktif challenge'ı iptal eder (§7.4).
 *
 * İptal GERİ ALINAMAZ ve Başarı Barı'ndan sabit 50 puan düşer. Bu
 * düşüş günlük alışkanlık hesabından bağımsızdır: `Math.trunc()`
 * kuralına tabi değildir, doğrudan ve anında uygulanır. `applyDeltaToBar`
 * sayesinde bar 0'ın altına inmez.
 */
export async function cancelChallenge(
  challenge: Challenge,
  today: IsoDate,
): Promise<void> {
  const cancelled: Challenge = {
    ...challenge,
    status: 'cancelled',
    cancelledAt: today,
  };
  await challengeRepository.upsert(cancelled);

  const progress = await getOrCreateProgressState();
  await progressStateRepository.upsert({
    ...progress,
    barValue: applyDeltaToBar(progress.barValue, -CHALLENGE_CANCEL_PENALTY),
  });
}

export interface NewChallengeInput {
  title: string;
  /** Kullanıcının girdiği sayı — birim `unit` ile belirlenir. */
  durationValue: number;
  unit: ChallengeDurationUnit;
  startDate: IsoDate;
}

/**
 * Yeni bir challenge oluşturur.
 *
 * Süre her zaman gün cinsinden saklanır; kullanıcının hangi birimi
 * seçtiği yalnızca ekranda "1 ay" yazabilmek için `durationInputUnit`
 * alanında tutulur (§7.4).
 */
export async function createChallenge(input: NewChallengeInput): Promise<Challenge> {
  const durationDays = toDurationDays(input.durationValue, input.unit);

  const challenge: Challenge = {
    id: crypto.randomUUID(),
    title: input.title,
    description: '',
    startDate: input.startDate,
    endDate: calculateEndDate(input.startDate, durationDays),
    durationDays,
    durationInputUnit: input.unit,
    status: 'active',
    completedDates: [],
  };

  await challengeRepository.upsert(challenge);
  return challenge;
}
